<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { mapsStore } from "@/_store/mapsStore";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      address: "",
      label: "",
      coordenates: null,
      normalized: false,
      postal_code: "",
    }),
  },
  error: {
    type: Boolean,
    default: false,
  },
  labelError: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    default: "vehicle-from-location-address",
  },
  placeholder: {
    type: String,
    default: "Indica la dirección a normalizar",
  },
  validateLabel: {
    type: String,
    default: "Validar",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showLabelInput: {
    type: Boolean,
    default: true,
  },
  labelInputId: {
    type: String,
    default: "",
  },
  labelPlaceholder: {
    type: String,
    default: "Resto de dirección (piso, puerta ...)",
  },
  labelMaxLength: {
    type: Number,
    default: 140,
  },
});

const addressInput = ref(props.modelValue?.address || "");
const addressInputRef = ref(null);
const labelInput = ref(props.modelValue?.label || "");
const suggestions = ref([]);
const suggestionOptionRefs = ref([]);
const isVisible = ref(false);
const isLoading = ref(false);
const snackbar = ref(null);
const maps = mapsStore();
const resolvedLabelInputId = computed(
  () => props.labelInputId || `${props.id}-label`
);

const canValidate = computed(
  () => String(addressInput.value || "").trim().length > 0
);
const showSuggestions = computed(
  () => isVisible.value && suggestions.value.length > 1
);
const hasAddressValue = computed(
  () => String(addressInput.value || "").trim().length > 0
);
const isAddressNormalized = computed(() => {
  if (!props.modelValue || typeof props.modelValue !== "object") return false;
  return !!props.modelValue.normalized;
});
const showNormalizeError = computed(
  () => hasAddressValue.value && !isAddressNormalized.value
);
const showInputError = computed(
  () => !!props.error || showNormalizeError.value
);
const showInputPrimary = computed(
  () => hasAddressValue.value && isAddressNormalized.value && !props.error
);

function normalizeSuggestion(item) {
  return {
    address: item?.address ? String(item.address).trim() : "",
    label: item?.label ? String(item.label).trim() : "",
    coordenates: item?.coordenates || null,
    normalized: !!item?.normalized,
    postal_code: item?.postal_code ? String(item.postal_code).trim() : "",
  };
}

function emitRawAddress(rawAddress) {
  emit("update:modelValue", {
    address: String(rawAddress || ""),
    label: labelInput.value || "",
    coordenates: null,
    normalized: false,
    postal_code: "",
  });
}

function onInput(value) {
  addressInput.value = value;
  suggestions.value = [];
  suggestionOptionRefs.value = [];
  isVisible.value = false;
  closeSnackbar();
  emitRawAddress(value);
}

function onLabelInput(value) {
  labelInput.value = String(value || "");
  emit("update:modelValue", {
    address: String(addressInput.value || ""),
    label: labelInput.value,
    coordenates: props.modelValue?.coordenates || null,
    normalized: !!props.modelValue?.normalized,
    postal_code: props.modelValue?.postal_code || "",
  });
}

function applySuggestion(item) {
  const normalized = normalizeSuggestion(item);
  if (!normalized.address) return;

  suggestions.value = [];
  suggestionOptionRefs.value = [];
  isVisible.value = false;
  closeSnackbar();
  addressInput.value = normalized.address;
  labelInput.value =
    props.modelValue?.label || normalized.label || labelInput.value || "";

  emit("update:modelValue", {
    ...normalized,
    label: labelInput.value,
  });
}

function selectSuggestion(selectedIndex) {
  const index = Number(selectedIndex);
  const selected = suggestions.value[index];
  if (!selected) return;
  applySuggestion(selected);
}

function handleClickOutside() {
  isVisible.value = false;
}

function onInputFocus() {
  if (suggestions.value.length > 1) {
    isVisible.value = true;
  }
}

function setSuggestionOptionRef(element, index) {
  if (!element) return;
  suggestionOptionRefs.value[index] = element;
}

function focusAddressInput() {
  addressInputRef.value?.focus();
}

function focusSuggestionOption(index) {
  const nextIndex = Number(index);
  if (nextIndex < 0 || nextIndex >= suggestions.value.length) return;

  nextTick(() => {
    const option = suggestionOptionRefs.value[nextIndex];
    option?.focus();
    option?.scrollIntoView?.({ block: "nearest" });
  });
}

function openSuggestionsWithKeyboard() {
  if (suggestions.value.length <= 1) return;
  isVisible.value = true;
  focusSuggestionOption(0);
}

function onInputKeydown(event) {
  if (event.key === "Enter") {
    event.preventDefault();
    validateAddress();
    return;
  }

  if (!showSuggestions.value) return;

  if (event.key === "ArrowDown") {
    event.preventDefault();
    focusSuggestionOption(0);
    return;
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();
    focusSuggestionOption(suggestions.value.length - 1);
  }
}

function onSuggestionKeydown(event, index) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    focusSuggestionOption((index + 1) % suggestions.value.length);
    return;
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();
    focusSuggestionOption(
      (index - 1 + suggestions.value.length) % suggestions.value.length
    );
    return;
  }

  if (event.key === "Enter") {
    event.preventDefault();
    selectSuggestion(index);
    return;
  }

  if (event.key === "Escape") {
    event.preventDefault();
    isVisible.value = false;
    focusAddressInput();
  }
}

function formatSuggestion(item) {
  const address = item?.address ? String(item.address) : "";
  const label = item?.label ? String(item.label) : "";
  return label ? `${address} - ${label}` : address;
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: `<ul><li>${
      message || "No se pudo validar la direccion"
    }</li></ul>`,
    type: "danger",
    color: "white",
  };
}

function extractResponseSuggestions(response) {
  const rawSuggestions = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.data)
    ? response.data.data
    : Array.isArray(response?.entity)
    ? response.entity
    : [];

  return Array.isArray(rawSuggestions[0])
    ? rawSuggestions.flat()
    : rawSuggestions;
}

function getErrorMessage(error) {
  if (typeof error === "string") return error;
  if (error?.error?.[0]?.message) return error.error[0].message;
  if (error?.message) return error.message;
  return "No se pudo validar la dirección";
}

async function validateAddress() {
  const query = String(addressInput.value || "").trim();
  if (!query || isLoading.value || props.disabled) return;

  isLoading.value = true;
  closeSnackbar();

  try {
    const response = await maps.normalizeAddress(query);
    const normalizedSuggestions = extractResponseSuggestions(response)
      .map(normalizeSuggestion)
      .filter((item) => !!item.address);

    suggestions.value = normalizedSuggestions;

    if (normalizedSuggestions.length === 1) {
      applySuggestion(normalizedSuggestions[0]);
      return;
    }

    if (normalizedSuggestions.length > 1) {
      isVisible.value = true;
      emitRawAddress(query);
      suggestionOptionRefs.value = [];
      openSuggestionsWithKeyboard();
      return;
    }

    isVisible.value = false;
    suggestionOptionRefs.value = [];
    showError("No se encontraron direcciones sugeridas");
    emitRawAddress(query);
  } catch (error) {
    suggestions.value = [];
    suggestionOptionRefs.value = [];
    isVisible.value = false;
    emitRawAddress(query);
    showError(getErrorMessage(error));
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => props.modelValue,
  (value) => {
    const nextAddress = value?.address || "";
    const nextLabel = value?.label || "";
    if (nextAddress !== addressInput.value) {
      addressInput.value = nextAddress;
      suggestions.value = [];
      suggestionOptionRefs.value = [];
      isVisible.value = false;
      closeSnackbar();
    }
    if (nextLabel !== labelInput.value) {
      labelInput.value = nextLabel;
    }
  },
  { deep: true }
);
</script>

<template>
  <div
    v-click-outside="handleClickOutside"
    class="auto-complete argon-address-normalize"
    :class="{ visible: showSuggestions }"
  >
    <div class="address-input-shell">
      <input
        :id="id"
        ref="addressInputRef"
        :value="addressInput"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': showInputError, 'is-normalized-primary': showInputPrimary }"
        :maxlength="180"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onInput($event.target.value)"
        @focus="onInputFocus"
        @keydown="onInputKeydown"
      />
      <button
        v-if="isLoading || !isAddressNormalized"
        v-tooltip="validateLabel"
        type="button"
        class="inline-validate-btn"
        :disabled="disabled || isLoading || !canValidate"
        :aria-label="validateLabel"
        @click="validateAddress"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-size"
          role="status"
          aria-hidden="true"
        ></span>
        <i v-else class="fa fa-check" aria-hidden="true"></i>
      </button>
    </div>

    <div
      v-show="showSuggestions"
      class="searcher-menu-auto-complete searcher-menu-config shadow rounded mt-1 p-1"
    >
      <div
        v-for="(item, index) in suggestions"
        :key="`${item.address}-${index}`"
        class="item"
      >
        <a
          :ref="(element) => setSuggestionOptionRef(element, index)"
          class="dropdown-item border-radius-md text-truncate"
          href="#"
          @click.prevent="selectSuggestion(index)"
          @keydown="onSuggestionKeydown($event, index)"
        >
          <b class="ellipsis">{{ formatSuggestion(item) }}</b>
        </a>
      </div>
    </div>

    <div v-if="showLabelInput" class="label-input-shell">
      <input
        :id="resolvedLabelInputId"
        :value="labelInput"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': labelError }"
        :maxlength="labelMaxLength"
        :placeholder="labelPlaceholder"
        :disabled="disabled"
        @input="onLabelInput($event.target.value)"
      />
    </div>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </div>
</template>

<style scoped>
.argon-address-normalize {
  position: relative;
}

.address-input-shell {
  position: relative;
}

 
.address-input-shell .form-control.is-normalized-primary:focus {
  border-color: var(--bs-primary) !important;
  border-bottom-color: var(--bs-primary) !important;
  background-image: none !important;
  padding-right: 0.75rem !important;
}

.label-input-shell {
  margin-top: 0.5rem;
}

.inline-validate-btn {
  position: absolute;
  top: 50%;
  right: 0.35rem;
  transform: translateY(-50%);
  z-index: 2;
  margin: 0;
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8392ab;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.86rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.12s ease, opacity 0.2s ease;
}

.inline-validate-btn:disabled {
  opacity: 0.45;
  color: #adb5bd;
  cursor: not-allowed;
}

.inline-validate-btn:not(:disabled):hover {
  color: #5e72e4;
  background-color: rgba(94, 114, 228, 0.12);
}

.inline-validate-btn:not(:disabled):active {
  transform: translateY(-50%) scale(0.96);
}

.inline-validate-btn:focus-visible {
  outline: none;
  color: #5e72e4;
  background-color: rgba(94, 114, 228, 0.12);
  box-shadow: 0 0 0 0.18rem rgba(94, 114, 228, 0.22);
}

.inline-validate-btn i {
  margin: 0;
}

.inline-validate-btn .spinner-size {
  width: 12px;
  height: 12px;
  border-width: 2px;
}


.searcher-menu-config {
  max-height: 200px;
  overflow-y: auto;
  opacity: 1;
  min-width: 100%;
  width: 100%;
}

.ellipsis {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-truncate {
  max-width: 100%;
}
</style>
